import React, { useEffect, useMemo, useState } from 'react';
import { contactsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import SearchBar from '../components/SearchBar';
import ConfirmDialog from '../components/ConfirmDialog';
import '../styles/PhoneBook.css';

const emptyForm = {
  name: '',
  phone: ''
};

function PhoneBook() {
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const [contacts, setContacts] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loading, setLoading] = useState(false);

  const canEdit = user?.role === 'owner' || user?.role === 'staff';

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await contactsAPI.getAll({ search: searchTerm });
      setContacts(response.data.contacts || []);
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to load phone book');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredContacts = useMemo(() => contacts, [contacts]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      if (editingId) {
        await contactsAPI.update(editingId, formData);
        success('Contact updated successfully');
      } else {
        await contactsAPI.create(formData);
        success('Contact saved successfully');
      }
      resetForm();
      fetchContacts();
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to save contact');
    }
  };

  const editContact = (contact) => {
    setFormData({
      name: contact.name || '',
      phone: contact.phone || ''
    });
    setEditingId(contact._id);
    setShowForm(true);
  };

  const deleteContact = async () => {
    if (!deleteTarget) return;
    try {
      await contactsAPI.delete(deleteTarget._id);
      success('Contact deleted successfully');
      fetchContacts();
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to delete contact');
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <main className="phonebook-container">
      <div className="page-header">
        <div>
          <h1>Phone Book</h1>
          <p className="phonebook-summary">Keep important contact numbers ready for your next event.</p>
        </div>
        {canEdit && (
          <button className="btn" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
            {showForm ? 'Cancel' : '+ Add Contact'}
          </button>
        )}
      </div>

      <div className="search-filter-row">
        <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder="Search by name or phone..." />
        <button className="btn btn-secondary" onClick={fetchContacts}>Search</button>
      </div>

      {showForm && canEdit && (
        <form className="form-container phonebook-form" onSubmit={handleSubmit}>
          <h2>{editingId ? 'Edit Contact' : 'Add Contact'}</h2>
          <div className="form-grid">
            <div className="form-group"><label>Name *</label><input name="name" value={formData.name} onChange={handleChange} required /></div>
            <div className="form-group"><label>Phone *</label><input name="phone" type="tel" value={formData.phone} onChange={handleChange} required /></div>
          </div>
          <button className="btn" type="submit">{editingId ? 'Update Contact' : 'Save Contact'}</button>
        </form>
      )}

      <section className="content-section">
        {loading ? <p className="phonebook-empty">Loading contacts...</p> : filteredContacts.length === 0 ? <p className="phonebook-empty">No contacts found.</p> : (
          <div className="phonebook-table-wrap">
            <table className={`phonebook-table ${canEdit ? 'has-actions' : 'read-only'}`}>
              <thead><tr><th>Name</th><th>Phone</th>{canEdit && <th>Actions</th>}</tr></thead>
              <tbody>{filteredContacts.map((contact) => (
                <tr key={contact._id}>
                  <td><strong>{contact.name}</strong></td>
                  <td><a href={`tel:${contact.phone}`}>{contact.phone}</a></td>
                  {canEdit && <td className="contact-actions"><button onClick={() => editContact(contact)}>Edit</button><button className="delete" onClick={() => setDeleteTarget(contact)}>Delete</button></td>}
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete contact?"
        message={deleteTarget ? `Remove ${deleteTarget.name} from the phone book?` : ''}
        onConfirm={deleteContact}
        onCancel={() => setDeleteTarget(null)}
      />
    </main>
  );
}

export default PhoneBook;
